import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BecomeProducerPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, fetchUser } = useAuth();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    story: '',
    region_id: '',
    address: '',
    phone: '',
    website: '',
    accepted_vendor_terms: false,
    accepted_terms: false,
    accepted_privacy: false,
  });

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await api.get('/catalog/regions');
        setRegions(res.data);
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    };
    fetchRegions();
  }, []);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (value) => {
    setFormData({
      ...formData,
      name: value,
      slug: generateSlug(value)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Devi accedere per diventare produttore');
      navigate('/login', { state: { from: { pathname: '/diventa-produttore' } } });
      return;
    }

    if (!formData.accepted_vendor_terms || !formData.accepted_terms || !formData.accepted_privacy) {
      toast.error('Devi accettare tutti i documenti legali per inviare la richiesta');
      return;
    }

    setLoading(true);

    try {
      await api.post('/producers/apply', formData);
      await fetchUser();
      toast.success('Richiesta inviata con successo! Attendi l\'approvazione.');
      navigate('/produttore/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'invio della richiesta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Accesso diretto a migliaia di clienti',
    'Gestione semplice di prodotti e ordini',
    'Commissioni trasparenti (20% base, 15% Pro)',
    'Supporto dedicato per i produttori',
    'Visibilità nel marketplace curato',
    'Pagamenti sicuri via Stripe'
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="become-producer-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-primary/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-text-primary">
              Diventa un Produttore DalSud
            </h1>
            <p className="mt-4 text-lg text-text-secondary">
              Unisciti alla nostra comunità di artigiani e agricoltori del Sud Italia. 
              Porta i tuoi prodotti a migliaia di clienti che cercano qualità e autenticità.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Benefits */}
          <div>
            <h2 className="font-heading text-2xl font-bold mb-6">Perché DalSud?</h2>
            <ul className="space-y-4">
              {features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-secondary" />
                  </div>
                  <span className="text-text-secondary">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Plans */}
            <div className="mt-10">
              <h3 className="font-heading text-xl font-bold mb-4">I Nostri Piani</h3>
              <div className="grid gap-4">
                <div className="bg-white p-6 rounded-xl border border-border">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold">Piano Gratuito</h4>
                    <span className="text-lg font-bold">€0/mese</span>
                  </div>
                  <ul className="text-sm text-text-secondary space-y-2">
                    <li>• Commissione 20%</li>
                    <li>• Fino a 10 prodotti</li>
                    <li>• Profilo base</li>
                  </ul>
                </div>
                <div className="bg-primary/5 p-6 rounded-xl border-2 border-primary">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-bold">Piano Pro</h4>
                      <span className="text-xs text-primary font-medium">Consigliato</span>
                    </div>
                    <span className="text-lg font-bold">€29.99/mese</span>
                  </div>
                  <ul className="text-sm text-text-secondary space-y-2">
                    <li>• Commissione ridotta al 15%</li>
                    <li>• Fino a 100 prodotti</li>
                    <li>• Badge Produttore Pro</li>
                    <li>• Priorità nei risultati</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Application Form */}
          <div>
            <div className="bg-white p-8 rounded-xl border border-border">
              <h2 className="font-heading text-2xl font-bold mb-6">Richiedi l'Approvazione</h2>

              {!isAuthenticated && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    Devi <Link to="/login" className="font-medium underline">accedere</Link> o{' '}
                    <Link to="/registrati" className="font-medium underline">registrarti</Link> per inviare la richiesta.
                  </p>
                </div>
              )}

              {user?.role === 'producer' && (
                <div className="mb-6 p-4 bg-secondary/10 border border-secondary/20 rounded-lg">
                  <p className="text-sm text-secondary">
                    Hai già un profilo produttore.{' '}
                    <Link to="/produttore/dashboard" className="font-medium underline">Vai alla Dashboard</Link>
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name">Nome Attività *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="es. Masseria del Sole"
                    required
                    className="mt-1"
                    data-testid="producer-name"
                  />
                </div>

                <div>
                  <Label htmlFor="slug">URL Profilo</Label>
                  <div className="flex items-center mt-1">
                    <span className="text-sm text-text-muted mr-2">dalsud.it/produttori/</span>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="masseria-del-sole"
                      required
                      data-testid="producer-slug"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="region_id">Regione</Label>
                  <Select 
                    value={formData.region_id || "none"} 
                    onValueChange={(v) => setFormData({ ...formData, region_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="producer-region">
                      <SelectValue placeholder="Seleziona regione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Seleziona...</SelectItem>
                      {regions.map(reg => (
                        <SelectItem key={reg.id} value={reg.id}>{reg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description">Descrizione *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Racconta chi sei e cosa produci..."
                    required
                    rows={3}
                    className="mt-1"
                    data-testid="producer-description"
                  />
                </div>

                <div>
                  <Label htmlFor="story">La Tua Storia</Label>
                  <Textarea
                    id="story"
                    value={formData.story}
                    onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                    placeholder="La storia della tua azienda e delle tue tradizioni..."
                    rows={4}
                    className="mt-1"
                    data-testid="producer-story"
                  />
                </div>

                <div>
                  <Label htmlFor="address">Indirizzo</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Via Roma 123, Napoli"
                    className="mt-1"
                    data-testid="producer-address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Telefono</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+39 081 123456"
                      className="mt-1"
                      data-testid="producer-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">Sito Web</Label>
                    <Input
                      id="website"
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://..."
                      className="mt-1"
                      data-testid="producer-website"
                    />
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 flex-shrink-0"
                      checked={formData.accepted_vendor_terms}
                      onChange={e => setFormData({ ...formData, accepted_vendor_terms: e.target.checked })}
                    />
                    <span className="text-sm text-text-secondary">
                      Accetto i{' '}
                      <Link to="/legal/termini-vendor" target="_blank" className="text-primary underline">
                        Termini Vendor
                      </Link>
                      {' '}*
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 flex-shrink-0"
                      checked={formData.accepted_terms}
                      onChange={e => setFormData({ ...formData, accepted_terms: e.target.checked })}
                    />
                    <span className="text-sm text-text-secondary">
                      Accetto i{' '}
                      <Link to="/legal/termini-condizioni" target="_blank" className="text-primary underline">
                        Termini e Condizioni
                      </Link>
                      {' '}*
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 flex-shrink-0"
                      checked={formData.accepted_privacy}
                      onChange={e => setFormData({ ...formData, accepted_privacy: e.target.checked })}
                    />
                    <span className="text-sm text-text-secondary">
                      Ho letto la{' '}
                      <Link to="/legal/privacy-policy" target="_blank" className="text-primary underline">
                        Privacy Policy
                      </Link>
                      {' '}*
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={
                    loading || !isAuthenticated || user?.role === 'producer' ||
                    !formData.accepted_vendor_terms || !formData.accepted_terms || !formData.accepted_privacy
                  }
                  data-testid="submit-producer-application"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      Invia Richiesta
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomeProducerPage;
